import type {
  WillOpenCardKey,
  WillOpenCardItemDto,
} from "src/api/request/requestService";

export interface ResolvedWillOpenCard extends WillOpenCardItemDto {
  displayName: string;
  modalComponent: string;
  description: string;
}

const registry: Record<WillOpenCardKey, Omit<ResolvedWillOpenCard, "key">> = {
  HEIGHT_INFO: {
    displayName: "Height / Weight / BMI",
    modalComponent: "HeightInfoModal",
    description: "Shows height, weight and BMI entry modal.",
  },
  HEIGHT_INFO_REQUEST: {
    displayName: "Height / Weight / BMI",
    modalComponent: "ServiceRequestVkiModal",
    description: "Shows VKI (height/weight/BMI) modal for request service flow.",
  },
  PROCESS_DIRECTION: {
    displayName: "Process Direction",
    modalComponent: "ProcessDirectionModal",
    description: "Opens direction selection for the requested service.",
  },
  PROCESS_DIRECTION_REQUEST: {
    displayName: "Process Direction",
    modalComponent: "ProcessDirectionModal",
    description: "Opens direction selection for the requested service.",
  },
  ASK_DOCTOR: {
    displayName: "Ask Doctor",
    modalComponent: "AskDoctorModal",
    description: "Doctor confirmation note/request modal.",
  },
  ASK_DOCTOR_REQUEST: {
    displayName: "Ask Doctor Request",
    modalComponent: "AskDoctorModal",
    description: "Doctor information request modal.",
  },
  PREGNANT_CONTROL: {
    displayName: "Pregnancy Control",
    modalComponent: "PregnantControlModal",
    description: "Pregnancy control acknowledgement modal.",
  },
  PREGNANT_CONTROL_REQUEST: {
    displayName: "Pregnancy Control Request",
    modalComponent: "PregnantControlModal",
    description: "Pregnancy control request modal.",
  },
  HIV: {
    displayName: "HIV",
    modalComponent: "HIVModal",
    description: "HIV related warning/consent modal.",
  },
  HIV_REQUEST: {
    displayName: "HIV",
    modalComponent: "HIVModal",
    description: "HIV related warning/consent modal.",
  },
  PACEMAKER: {
    displayName: "Pacemaker",
    modalComponent: "PacemakerModal",
    description: "Pacemaker info / safety confirmation modal.",
  },
  PACEMAKER_CONTROL: {
    displayName: "Pacemaker",
    modalComponent: "PacemakerModal",
    description: "Pacemaker info / safety confirmation modal.",
  },
  PACEMAKER_CONTROL_REQUEST: {
    displayName: "Pacemaker",
    modalComponent: "PacemakerModal",
    description: "Pacemaker info / safety confirmation modal.",
  },
  CONSULTATION: {
    displayName: "Consultation",
    modalComponent: "ConsultationModal",
    description: "Consultation request information modal.",
  },
  CONSULTATION_REQUEST: {
    displayName: "Consultation",
    modalComponent: "ConsultationModal",
    description: "Consultation request information modal.",
  },
  LAB_REQUEST: {
    displayName: "Lab Request",
    modalComponent: "LabRequestModal",
    description: "Laboratory request extra information modal.",
  },
  PATHOLOGY: {
    displayName: "Pathology",
    modalComponent: "PathologyModal",
    description: "Pathology preparation/consent modal.",
  },
  PATHOLOGY_REQUEST: {
    displayName: "Pathology",
    modalComponent: "PathologyModal",
    description: "Pathology preparation/consent modal.",
  },
  PATIENT_FILE_CHANGE: {
    displayName: "Patient File Change",
    modalComponent: "PatientComingFileChangeModal",
    description: "Patient file modification request modal.",
  },
  CONTRAST: {
    displayName: "Contrast",
    modalComponent: "ServiceAddKonstrastModal",
    description: "Contrast service additional info / consent modal.",
  },
  CONTRAST_REQUEST: {
    displayName: "Contrast Request",
    modalComponent: "ServiceRequestKonstrastModal",
    description: "Contrast request information modal.",
  },
  EXAMINATION_REQUEST: {
    displayName: "Examination Request",
    modalComponent: "InvestigationRequestNoteModal",
    description: "Examination request note / preparation modal.",
  },
  VKI_ADD: {
    displayName: "VKI (Add Service)",
    modalComponent: "ServiceAddVkiModal",
    description: "VKI general health information modal for add service.",
  },
  VKI_REQUEST: {
    displayName: "VKI (Request Service)",
    modalComponent: "ServiceRequestVkiModal",
    description: "VKI general health information modal for request service.",
  },
  EGFR: {
    displayName: "eGFR",
    modalComponent: "ServiceAddEgfrModal",
    description: "Pediatric eGFR hesaplama formu.",
  },
  EGFR_REQUEST: {
    displayName: "eGFR",
    modalComponent: "ServiceAddEgfrModal",
    description: "Pediatric eGFR hesaplama formu.",
  },
  MEDICAL_GENETICS_REQUEST: {
    displayName: "Medical Genetics",
    modalComponent: "InvestigationRequestNoteModal",
    description: "Medical genetics information / note modal.",
  },
};

/**
 * Maps backend payload to UI friendly structure. Unknown keys are ignored.
 */
export const resolveWillOpenCards = (
  payload?: WillOpenCardItemDto[] | null
): ResolvedWillOpenCard[] => {
  if (!payload || !Array.isArray(payload)) return [];

  return payload
    .map((item) => {
      const normalizedKey = String((item as any)?.key ?? "")
        .trim() as WillOpenCardKey;
      if (!normalizedKey) return null;

      const match = registry[normalizedKey];
      if (!match) return null;
      return {
        ...item,
        key: normalizedKey,
        displayName: match.displayName,
        modalComponent: match.modalComponent,
        description: match.description,
      };
    })
    .filter(Boolean) as ResolvedWillOpenCard[];
};

/**
 * Example payload we expect from backend for documentation and tests.
 */
export const exampleWillOpenCardPayload: WillOpenCardItemDto[] = [
  { key: "LAB_REQUEST", reason: "Panel requires lab note" },
  { key: "PATIENT_FILE_CHANGE", reason: "File update is mandatory" },
  { key: "PROCESS_DIRECTION", reason: "Direction selection required" },
];

